import { trpc } from '@/main'
import { useSuspenseQuery, type QueryClient } from '@tanstack/react-query'
import { useDeferredValue } from 'react'
import { deriveProfiles, useRootSearch } from './use-root-search'
import type { RootSearch } from '@/routes/__root'

/**
 * Loader-side prefetch of the PERSISTENT offer chrome (OffersLayout / ApplicantOffersLayout applicants
 * panel + breadcrumb): the viewer-resolved `getJob` detail and the applicants-infinite list (carries the
 * applicant's own `viewerMatch`). This lives in the `offers.$offerId` layout loader — that route renders
 * real content (the incomplete-offer auto-open + the tab Outlet), so per AGENTS.md it owns the
 * offerId-keyed chrome its subtree reads (the tab leaves no longer duplicate it). All deferred: the
 * panel reads via suspense.
 */
export function prefetchOfferChrome(
  queryClient: QueryClient,
  search: RootSearch,
  params: Parameters<typeof deriveProfiles>[1],
) {
  const { first } = deriveProfiles(search, params, false)
  const source =
    first?.type === 'job' && 'source' in first && first.source === 'integrants' ? 'integrants' : 'described'
  queryClient.prefetchQuery(trpc.jobs.getJob.queryOptions({ jobId: Number(params.offerId) }))
  queryClient.prefetchInfiniteQuery(
    trpc.jobs.getJobIntegrants.infiniteQueryOptions(
      { jobId: Number(params.offerId), type: search.type, o: search.o, od: search.od, q: '', source },
      { getNextPageParam: (lastPage) => lastPage.nextCursor },
    ),
  )
}

/**
 * Suspense data hook for the offer board/distribution subviews (its only consumers). Reads the
 * applicant indicators via `useSuspenseQuery` so the subview's <Suspense fallback> owns the loading
 * skeleton. `type` is deferred so changing the type filter keeps the PREVIOUS view on screen while the
 * new data loads (keep-previous UX, mirroring the report). Requires a concrete `offerId` — the guard
 * component handles the no-offer case before mounting this hook (useSuspenseQuery can't take skipToken).
 */
export function useOffer(offerId: number) {
  const { type } = useRootSearch()
  const deferredType = useDeferredValue(type)
  const { data: offerApplicants, isFetching } = useSuspenseQuery(
    trpc.companies.getOfferApplicantIndicators.queryOptions({ offerId, type: deferredType }),
  )
  return { offerApplicants, isFetching }
}
